import {
	AccountBalance,
	ArrowForward,
	AttachMoneyOutlined,
	CheckCircle,
	ChevronLeft,
	ChevronRight,
	Close,
	CurrencyExchange,
	DeleteOutlined,
	DirectionsCar,
	EditOutlined,
	ExpandMore,
	MailOutline,
	Payments as PaymentsIcon,
	Search,
	Storefront,
	TrendingUp,
	WarningAmberRounded
} from '@mui/icons-material';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { QrCode } from 'lucide-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorScreen, Loading } from '../../../components';
import { PagamentoDTO, RevendaPagamentoDTO } from '../../../pages/payments/payments.types';
import { useToastStore } from '../../../stores';
import { colorPrimary, colorSubText, textFieldStyle } from '../../../theme';
import { maskCurrencyBRL } from '../../../utils/mask/MaskUtils';
import { QrCodeModal } from '../../shopkeeper/components/QrCodeModal';
import { StatCard } from '../../shopkeeper/components/StatCard';
import { SubmitButton } from '../components/ButtonComponent';
import { RevendaInfoDialog } from '../components/RevendaInfoDialog';
import { AtualizarCobrancaAnuncioDTO, AtualizarPagamentoAdminDTO, DashBoardPagamentosDTO, RevendaAtivaItem, RevendaInfoDetailsDTO } from '../dto/CompanyDTO';
import { atualizarCobrancaAnuncio, atualizarPagamento, atualizarRevendaCompradora, deletePagamento, fetchPayments, fetchPaymentsDashboard, fetchRevendasAtivas, renovarPagamento } from '../repository/ComapnyRepository';

// ─── Helpers ─────────────────────────────────────────────────
const formatDate = (dateStr: string | null | undefined): string => {
	if (!dateStr) return '-';
	return new Date(dateStr).toLocaleDateString('pt-BR');
};

const formatCurrency = (value: number): string => {
	return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// ─── Header cell style reutilizável ─────────────────────────
const headerCellSx = {
	fontSize: '0.725rem',
	fontWeight: 700,
	color: '#94a3b8',
	textTransform: 'uppercase' as const,
	letterSpacing: 1.5,
	borderBottom: '1px solid #f1f5f9',
	py: 2,
	whiteSpace: 'nowrap' as const,
};

// ═════════════════════════════════════════════════════════════
//  COMPONENT
// ═════════════════════════════════════════════════════════════
const Payments: FC = () => {
	const muiTheme = useTheme();
	const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
	const isTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));
	const { setToast } = useToastStore();

	// ── State ───────────────────────────────────────────────
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const [payments, setPayments] = useState<PagamentoDTO[]>([]);
	const [dashboard, setDashboard] = useState<DashBoardPagamentosDTO | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeFilter, setActiveFilter] = useState<'all' | 'open' | 'closed'>('all');
	const [expandedRows, setExpandedRows] = useState<number[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;
	const searchInput = useRef('');
	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Date filters (refs para evitar re-fetch ao navegar meses no calendário)
	const defaultStart = new Date();
	defaultStart.setMonth(defaultStart.getMonth() - 1);
	const defaultStartStr = defaultStart.toISOString().split('T')[0];
	const defaultEndStr = new Date().toISOString().split('T')[0];
	const [startDate, setStartDate] = useState(defaultStartStr);
	const [endDate, setEndDate] = useState(defaultEndStr);
	const startDateRef = useRef(defaultStartStr);
	const endDateRef = useRef(defaultEndStr);

	// Modals
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [paymentToDelete, setPaymentToDelete] = useState<number | null>(null);

	const [remakeDialogOpen, setRemakeDialogOpen] = useState(false);
	const [paymentToRemake, setPaymentToRemake] = useState<PagamentoDTO | null>(null);

	const [paidDialogOpen, setPaidDialogOpen] = useState(false);
	const [paymentToPay, setPaymentToPay] = useState<PagamentoDTO | null>(null);
	const [paymentValue, setPaymentValue] = useState('');
	const [payDateValue, setPayDateValue] = useState('');

	const [buyerDialogOpen, setBuyerDialogOpen] = useState(false);
	const [buyerData, setBuyerData] = useState<RevendaInfoDetailsDTO | null>(null);

	const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
	const [qrCodeData, setQrCodeData] = useState<string | null>(null);

	const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
	const [paymentToUpdate, setPaymentToUpdate] = useState<PagamentoDTO | null>(null);
	const [vehiclesToRemove, setVehiclesToRemove] = useState<number[]>([]);
	const [newValVenda, setNewValVenda] = useState('');
	const [newValCobrado, setNewValCobrado] = useState('');

	const [compradorDialogOpen, setCompradorDialogOpen] = useState(false);
	const [paymentToChangeBuyer, setPaymentToChangeBuyer] = useState<PagamentoDTO | null>(null);
	const [revendaSearch, setRevendaSearch] = useState('');
	const [revendas, setRevendas] = useState<RevendaAtivaItem[]>([]);
	const [selectedRevendaId, setSelectedRevendaId] = useState<number | null>(null);
	const [revendaSearchLoading, setRevendaSearchLoading] = useState(false);
	const [updatingBuyer, setUpdatingBuyer] = useState(false);
	const [confirmCompradorOpen, setConfirmCompradorOpen] = useState(false);

	// Pagination
	const totalPages = Math.max(1, Math.ceil(payments.length / itemsPerPage));
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedPayments = payments.slice(startIndex, endIndex);

	// ── Handlers ────────────────────────────────────────────
	const toggleRowExpansion = (id: number) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
	};

	const handleFilterChange = (filter: 'all' | 'open' | 'closed') => {
		setActiveFilter((prev) => (prev === filter ? 'all' : filter));
		setCurrentPage(1);
	};

	// Delete
	const handleDeleteConfirm = useCallback((id: number) => {
		setPaymentToDelete(id);
		setDeleteDialogOpen(true);
	}, []);

	// Remake
	const handleRemakeConfirm = useCallback((payment: PagamentoDTO) => {
		setPaymentToRemake(payment);
		setRemakeDialogOpen(true);
	}, []);


	// Set as paid
	const handleOpenPaidDialog = useCallback((payment: PagamentoDTO) => {
		setPaymentToPay(payment);
		setPaymentValue('');
		setPayDateValue('');
		setPaidDialogOpen(true);
	}, []);

	// Buyer details
	const handleOpenBuyerDialog = useCallback((revenda: RevendaPagamentoDTO) => {
		setBuyerData({
			nome: revenda.revenda,
			documento: revenda.documento,
			email: revenda.email,
			telefone: revenda.contato,
			logradouro: revenda.endereco,
			bairro: revenda.bairro,
			cidade: revenda.local,
			cep: revenda.cep,
		});
		setBuyerDialogOpen(true);
	}, []);

	// QR Code
	const handleOpenQrCode = useCallback(
		(payment: PagamentoDTO) => {
			if (!payment.qrcode) {
				setToast({ message: 'Registro de PIX não encontrado! Contate o administrador.', variant: 'error' });
				return;
			}
			setQrCodeData(payment.qrcode);
			setQrCodeModalOpen(true);
		},
		[setToast]
	);

	// Update charge
	const handleOpenUpdateDialog = useCallback((payment: PagamentoDTO) => {
		setPaymentToUpdate(payment);
		setVehiclesToRemove([]);
		setNewValVenda('');
		setNewValCobrado('');
		setUpdateDialogOpen(true);
	}, []);

	const handleOpenCompradorDialog = useCallback((payment: PagamentoDTO) => {
		setPaymentToChangeBuyer(payment);
		setRevendaSearch('');
		setRevendas([]);
		setSelectedRevendaId(null);
		setRevendaSearchLoading(true);
		setCompradorDialogOpen(true);
	}, []);

	const handleCloseCompradorDialog = useCallback(() => {
		if (updatingBuyer) return;
		setConfirmCompradorOpen(false);
		setCompradorDialogOpen(false);
		setPaymentToChangeBuyer(null);
		setRevendaSearch('');
		setRevendas([]);
		setSelectedRevendaId(null);
	}, [updatingBuyer]);

	const handleToggleVehicleRemoval = useCallback((idVeiculo: number) => {
		setVehiclesToRemove((prev) =>
			prev.includes(idVeiculo) ? prev.filter((id) => id !== idVeiculo) : [...prev, idVeiculo]
		);
	}, []);

	// Pagination
	const handleNextPage = () => {
		if (currentPage < totalPages) setCurrentPage((p) => p + 1);
	};
	const handlePrevPage = () => {
		if (currentPage > 1) setCurrentPage((p) => p - 1);
	};

const fetchPaymentsData = useCallback(async () => {
	try {
		setIsLoading(true);
		const statusFilter = activeFilter === 'open' ? 'Pendente' : activeFilter === 'closed' ? 'Pago' : '';
		const result = await fetchPayments(searchInput.current, startDateRef.current, endDateRef.current, statusFilter);
		if(!result?.success) {
			setToast({variant:"error", message: result.message});
			return;
		}
		setPayments(result.data);
	} catch (error) {
		setToast({variant:"error", message: "Erro. Contate o administrador."});
		setError(true);
	} finally {
		setIsLoading(false);
	}
}, [activeFilter, setToast]);

useEffect(() => {
	if (!compradorDialogOpen) return;

	let cancelled = false;
	const timer = setTimeout(async () => {
		try {
			setRevendaSearchLoading(true);
			const term = revendaSearch.trim();
			const result = await fetchRevendasAtivas(term || undefined);
			if (cancelled) return;
			if (!result?.success) {
				setToast({ variant: 'error', message: result?.message ?? 'Erro ao buscar revendas.' });
				setRevendas([]);
				return;
			}
			setRevendas(result.data ?? []);
		} catch {
			if (cancelled) return;
			setToast({ variant: 'error', message: 'Erro. Contate o administrador.' });
			setRevendas([]);
		} finally {
			if (!cancelled) setRevendaSearchLoading(false);
		}
	}, 700);

	return () => {
		cancelled = true;
		clearTimeout(timer);
	};
}, [compradorDialogOpen, revendaSearch, setToast]);

const handleAtualizarComprador = useCallback(async () => {
	if (!paymentToChangeBuyer || selectedRevendaId == null) return;
	try {
		setUpdatingBuyer(true);
		const result = await atualizarRevendaCompradora(paymentToChangeBuyer.id_anuncio, selectedRevendaId);
		if (!result?.success) {
			setToast({ variant: 'error', message: result?.message ?? 'Erro ao atualizar comprador.' });
			return;
		}
		setToast({ variant: 'success', message: result?.message ?? 'Revenda compradora atualizada com sucesso.' });
		setConfirmCompradorOpen(false);
		setCompradorDialogOpen(false);
		setPaymentToChangeBuyer(null);
		setRevendaSearch('');
		setRevendas([]);
		setSelectedRevendaId(null);
		fetchPaymentsData();
	} catch {
		setToast({ variant: 'error', message: 'Erro. Contate o administrador.' });
	} finally {
		setUpdatingBuyer(false);
	}
}, [paymentToChangeBuyer, selectedRevendaId, setToast, fetchPaymentsData]);

const handleDeletePayment = useCallback(async () => {
	if (paymentToDelete === null) return;
	try {
		setIsLoading(true);
		const result = await deletePagamento(paymentToDelete);
		if (!result?.success) {
			setToast({ variant: 'error', message: result?.message ?? 'Erro ao remover pagamento.' });
			return;
		}
		setToast({ message: result?.message ?? 'Pagamento removido com sucesso!', variant: 'success' });
		await fetchPaymentsData();
		setDeleteDialogOpen(false);
		setPaymentToDelete(null);
	} catch (error) {
		setToast({ variant: 'error', message: 'Erro. Contate o administrador.' });
	} finally {
		setIsLoading(false);
	}
}, [paymentToDelete, setToast, fetchPaymentsData]);

const handleSubmitUpdateCobranca = useCallback(async () => {
	if (!paymentToUpdate) return;

	const valVendaNum = Number(newValVenda.replace(/\D/g, '')) / 100;
	const valCobradoNum = Number(newValCobrado.replace(/\D/g, '')) / 100;

	if (!valVendaNum || valVendaNum <= 0) {
		setToast({ message: 'Informe um valor de venda válido.', variant: 'warning' });
		return;
	}
	if (!valCobradoNum || valCobradoNum <= 0) {
		setToast({ message: 'Informe um valor de cobrança válido.', variant: 'warning' });
		return;
	}

	const remainingVehicles = paymentToUpdate.veiculos.length - vehiclesToRemove.length;
	if (remainingVehicles < 1) {
		setToast({ message: 'Pelo menos 1 veículo deve permanecer no anúncio.', variant: 'warning' });
		return;
	}

	const dto: AtualizarCobrancaAnuncioDTO = {
		id_anuncio: paymentToUpdate.id_anuncio,
		val_venda: valVendaNum,
		val_cobrado: valCobradoNum,
		veiculos_remover: vehiclesToRemove,
	};

	try {
		setIsLoading(true);
		const result = await atualizarCobrancaAnuncio(dto);
		if (!result?.success) {
			setToast({ variant: 'error', message: result?.message ?? 'Erro ao atualizar cobrança.' });
			return;
		}
		setToast({ variant: 'success', message: result.message });
		setUpdateDialogOpen(false);
		setPaymentToUpdate(null);
		fetchPaymentsData();
	} catch {
		setToast({ variant: 'error', message: 'Erro. Contate o administrador.' });
	} finally {
		setIsLoading(false);
	}
}, [paymentToUpdate, newValVenda, newValCobrado, vehiclesToRemove, setToast, fetchPaymentsData]);

const handleAtualizarPagamento = useCallback(async (dto: AtualizarPagamentoAdminDTO) => {
	try {
		setIsLoading(true);
		const result = await atualizarPagamento(dto);
		if (!result?.success) {
			setToast({ variant: 'error', message: result.message });
			return;
		}
		setToast({ variant: 'success', message: result.message });
		fetchPaymentsData();
		setRemakeDialogOpen(false);
	} catch (error) {
		setToast({ variant: 'error', message: 'Erro. Contate o administrador.' });
	} finally {
		setIsLoading(false);
	}
}, [setToast, fetchPaymentsData]);

const handleSetPaid = useCallback(async () => {
	if (!paymentValue || !payDateValue) {
		setToast({ message: 'Necessário digitar data e valor do pagamento.', variant: 'warning' });
		return;
	}
	if (!paymentToPay) return;

	const valorNum = Number(paymentValue.replace(/\D/g, '')) / 100;
	if (!valorNum || valorNum <= 0) {
		setToast({ message: 'Informe um valor válido.', variant: 'warning' });
		return;
	}

	await handleAtualizarPagamento({
		id_pagamento: paymentToPay.id_pagamento,
		val_pagamento: valorNum,
		data_pagamento: payDateValue.includes('T') ? payDateValue : `${payDateValue}T00:00:00Z`,
	});
	setPaidDialogOpen(false);
	setPaymentToPay(null);
	setPaymentValue('');
	setPayDateValue('');
}, [paymentValue, payDateValue, paymentToPay, handleAtualizarPagamento, setToast]);

const handleRenovarPagamento = useCallback(async (id_pagamento: number) => {
	setIsLoading(true);
	try {
		const result = await renovarPagamento(id_pagamento);
		if(!result?.success){
			setToast({variant:"error", message: result.message});
			return;
		}
		setToast({variant:"success", message: result.message});
		fetchPaymentsData();
		setRemakeDialogOpen(false);
	} catch (error) {
		setToast({variant:"error", message: "Erro. Contate o administrador."});
	} finally {
		setIsLoading(false);
	}
}, [setToast, fetchPaymentsData]);

const fetchDashboard = useCallback(async () => {
	try {
		const result = await fetchPaymentsDashboard();
		if(!result?.success) {
			setToast({variant:"error", message: result.message});
			return;
		}
		setDashboard(result.data);
	} catch (error) {
		setToast({variant:"error", message: "Erro. Contate o administrador."});
	}
}, [setToast]);


// ── Effects (re-fetch automático quando datas ou filtro de status mudam) ──
useEffect(() => {
	const preLoadData = async () => {
		setIsLoading(true);
		await fetchPaymentsData();
		await fetchDashboard();
		setIsLoading(false);
	}
	preLoadData();
}, [fetchPaymentsData, fetchDashboard]);

	useEffect(() => {
		setCurrentPage(1);
	}, [payments]);

	useEffect(() => {
		if (startDate && endDate && new Date(startDate).getTime() > new Date(endDate).getTime()) {
			setEndDate(startDate);
			endDateRef.current = startDate;
		}
	}, [startDate, endDate]);

	// ── Early returns ───────────────────────────────────────
	if (isLoading) return <Loading fullScreen />;
	if (error) return <ErrorScreen fullScreen />;

	// ═════════════════════════════════════════════════════════
	//  RENDER
	// ═════════════════════════════════════════════════════════
	return (
		<>
			{/* ── Dialogs ────────────────────────────────────── */}

			{/* Delete Confirmation */}
			<Dialog open={deleteDialogOpen} onClose={() => !isLoading && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Confirmação</DialogTitle>
				<DialogContent>
					<Typography>Deseja realmente apagar esta cobrança?</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setDeleteDialogOpen(false)} disabled={isLoading} sx={{ color: '#64748b' }}>
						Não
					</Button>
					<SubmitButton title="Sim" onClick={handleDeletePayment} disabled={isLoading} maxWidth={100} />
				</DialogActions>
			</Dialog>

			{/* Remake Confirmation */}
			<Dialog open={remakeDialogOpen} onClose={() => setRemakeDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Confirmação</DialogTitle>
				<DialogContent>
					<Typography mb={2}>Deseja realmente refazer esta cobrança?</Typography>
					<Typography fontWeight={700} fontSize="0.875rem" color="#b91c1c">
						Atenção:
					</Typography>
					<Typography fontSize="0.875rem" color="#64748b">
						Isso apagará qualquer registro de pagamento dessa cobrança e irá gerar um novo QR Code do PIX.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setRemakeDialogOpen(false)} sx={{ color: '#64748b' }}>
						Não
					</Button>
					<SubmitButton title="Sim" onClick={ async () => await handleRenovarPagamento(paymentToRemake?.id_pagamento ?? 0)} disabled={isLoading} maxWidth={100}/>

				</DialogActions>
			</Dialog>

			{/* Set as Paid */}
			<Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Efetuar Baixa de Pagamento</DialogTitle>
				<DialogContent>
					<Stack spacing={3} pt={1}>
						<TextField
							label="Valor do Pagamento"
							value={paymentValue}
							placeholder="R$ 0"
							fullWidth
							sx={textFieldStyle}
							type="text"
							onChange={(e) => setPaymentValue(maskCurrencyBRL(e.target.value))}
						/>
						<TextField
							label="Data de Pagamento"
							type="date"
							value={payDateValue}
							onChange={(e) => setPayDateValue(e.target.value)}
							fullWidth
							sx={textFieldStyle}
							InputLabelProps={{ shrink: true }}
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setPaidDialogOpen(false)} sx={{ color: '#64748b' }}>
						Cancelar
					</Button>
					<SubmitButton title="Concluir baixa" onClick={handleSetPaid} disabled={isLoading} maxWidth={200}/>
				</DialogActions>
			</Dialog>

		{/* Buyer Details */}
		<RevendaInfoDialog
			open={buyerDialogOpen}
			onClose={() => setBuyerDialogOpen(false)}
			data={buyerData}
		/>

			{/* QR Code Modal (reusable) */}
			<QrCodeModal
				open={qrCodeModalOpen}
				onClose={() => setQrCodeModalOpen(false)}
				qrCodeData={qrCodeData}
				title="Taxa Canal Do Repasse - PIX"
			/>

			{/* Update Charge Dialog */}
			<Dialog
				open={updateDialogOpen}
				onClose={() => !isLoading && setUpdateDialogOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: 3 } }}
			>
				<DialogTitle sx={{ fontWeight: 700 }}>Atualizar Cobrança do Anúncio</DialogTitle>
				<DialogContent>
					<Stack spacing={3} pt={1}>
						<Box>
							<Typography fontWeight={700} fontSize="0.875rem" color="#b91c1c" mb={0.5}>
								Atenção:
							</Typography>
							<Typography fontSize="0.8rem" color="#64748b">
								Esta ação irá remover todos os registros de propostas e pagamentos deste anúncio e gerar
								uma nova cobrança PIX com o valor informado.
							</Typography>
						</Box>

						{paymentToUpdate && paymentToUpdate.veiculos.length > 1 && (
							<Box>
								<Typography fontSize="0.825rem" fontWeight={700} mb={1}>
									Veículos no anúncio
								</Typography>
								<Stack spacing={1}>
									{paymentToUpdate.veiculos.map((veiculo) => {
										const isMarked = vehiclesToRemove.includes(veiculo.id_veiculo);
										const canRemove =
											paymentToUpdate.veiculos.length - vehiclesToRemove.length > 1 || !isMarked;
										return (
											<Box
												key={veiculo.id_veiculo}
												display="flex"
												alignItems="center"
												justifyContent="space-between"
												p={1.5}
												borderRadius={1.5}
												border={`1px solid ${isMarked ? '#fecaca' : '#f1f5f9'}`}
												bgcolor={isMarked ? '#fef2f2' : 'white'}
												sx={{
													opacity: isMarked ? 0.6 : 1,
													transition: 'all 0.2s',
												}}
											>
												<Box>
													<Typography fontSize="0.8rem" fontWeight={700}>
														{veiculo.marca} {veiculo.modelo}
													</Typography>
													<Typography fontSize="0.7rem" color="#64748b">
														{veiculo.placa} &bull; {veiculo.ano_fabricacao}/{veiculo.ano_modelo}
													</Typography>
												</Box>
												<Tooltip
													title={
														!canRemove
															? 'Pelo menos 1 veículo deve permanecer'
															: isMarked
																? 'Desfazer remoção'
																: 'Remover do anúncio'
													}
													arrow
												>
													<span>
														<IconButton
															size="small"
															disabled={!canRemove}
															onClick={() => handleToggleVehicleRemoval(veiculo.id_veiculo)}
															sx={{
																width: 28,
																height: 28,
																bgcolor: isMarked ? '#059669' : '#dc2626',
																color: 'white',
																borderRadius: 1,
																'&:hover': {
																	bgcolor: isMarked ? '#047857' : '#b91c1c',
																},
																'&:disabled': {
																	bgcolor: '#e2e8f0',
																	color: '#94a3b8',
																},
															}}
														>
															<Close sx={{ fontSize: 14 }} />
														</IconButton>
													</span>
												</Tooltip>
											</Box>
										);
									})}
								</Stack>
								{vehiclesToRemove.length > 0 && (
									<Typography fontSize="0.75rem" color="#b91c1c" mt={1} fontWeight={600}>
										{vehiclesToRemove.length} veículo(s) será(ão) removido(s) do anúncio
									</Typography>
								)}
							</Box>
						)}

						<TextField
							label="Valor de Venda atualizado"
							value={newValVenda}
							placeholder="R$ 0"
							fullWidth
							sx={textFieldStyle}
							type="text"
							onChange={(e) => setNewValVenda(maskCurrencyBRL(e.target.value))}
						/>
						<TextField
							label="Valor da Cobrança atualizado"
							value={newValCobrado}
							placeholder="R$ 0"
							fullWidth
							sx={textFieldStyle}
							type="text"
							onChange={(e) => setNewValCobrado(maskCurrencyBRL(e.target.value))}
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setUpdateDialogOpen(false)} disabled={isLoading} sx={{ color: '#64748b' }}>
						Cancelar
					</Button>
					<SubmitButton
						title="Atualizar Cobrança"
						onClick={handleSubmitUpdateCobranca}
						disabled={isLoading}
						maxWidth={220}
					/>
				</DialogActions>
			</Dialog>

			<Dialog
				open={compradorDialogOpen}
				onClose={handleCloseCompradorDialog}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 4,
						overflow: 'hidden',
						boxShadow: '0 25px 50px -12px rgba(0,0,0,.15)',
					},
				}}
			>
				<Box
					sx={{
						background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
						px: 3,
						pt: 2.5,
						pb: 2.5,
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Box
						sx={{
							width: 44,
							height: 44,
							borderRadius: 2,
							bgcolor: 'rgba(255,255,255,.2)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<Storefront sx={{ fontSize: 22, color: '#fff' }} />
					</Box>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography fontWeight={700} fontSize="1.05rem" color="#fff">
							Atualizar comprador
						</Typography>
						<Typography fontSize="0.75rem" color="rgba(255,255,255,.8)">
							Busque pelo e-mail e selecione a nova revenda
						</Typography>
					</Box>
					<IconButton
						onClick={handleCloseCompradorDialog}
						disabled={updatingBuyer}
						size="small"
						sx={{ color: 'rgba(255,255,255,.75)', '&:hover': { color: '#fff' } }}
					>
						<Close sx={{ fontSize: 18 }} />
					</IconButton>
				</Box>

				<DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
					<Stack spacing={2.25}>
						<Box
							sx={{
								p: 1.75,
								borderRadius: 2,
								bgcolor: '#f8fafc',
								border: '1px solid #e2e8f0',
							}}
						>
							<Typography
								fontSize="0.68rem"
								fontWeight={700}
								color="#94a3b8"
								textTransform="uppercase"
								letterSpacing={0.8}
								mb={0.75}
							>
								Comprador atual
							</Typography>
							<Stack direction="row" alignItems="center" spacing={1.25}>
								<Box
									sx={{
										width: 32,
										height: 32,
										borderRadius: 1.5,
										bgcolor: '#e2e8f0',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Storefront sx={{ fontSize: 16, color: '#64748b' }} />
								</Box>
								<Typography fontSize="0.875rem" fontWeight={600} color="#1e293b">
									{paymentToChangeBuyer?.revenda?.revenda || 'Revenda não informada'}
								</Typography>
							</Stack>
						</Box>

						<Box>
							<Typography
								fontSize="0.68rem"
								fontWeight={700}
								color="#94a3b8"
								textTransform="uppercase"
								letterSpacing={0.8}
								mb={1}
							>
								Buscar revenda
							</Typography>
							<TextField
								size="small"
								label="E-mail"
								placeholder="Digite o e-mail da revenda"
								value={revendaSearch}
								onChange={(e) => {
									setRevendaSearch(e.target.value);
									setSelectedRevendaId(null);
									setRevendaSearchLoading(true);
								}}
								fullWidth
								sx={textFieldStyle}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<MailOutline sx={{ fontSize: 18, color: '#94a3b8' }} />
										</InputAdornment>
									),
								}}
							/>
							<Typography fontSize="0.7rem" color="#94a3b8" mt={0.75}>
								A busca inicia automaticamente após você parar de digitar
							</Typography>
						</Box>

						<Box>
							<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
								<Typography
									fontSize="0.68rem"
									fontWeight={700}
									color="#94a3b8"
									textTransform="uppercase"
									letterSpacing={0.8}
								>
									Revendas encontradas
								</Typography>
								{!revendaSearchLoading && (
									<Chip
										size="small"
										label={`${revendas.length}`}
										sx={{
											height: 20,
											fontSize: '0.68rem',
											fontWeight: 700,
											bgcolor: '#ecfeff',
											color: '#0e7490',
										}}
									/>
								)}
							</Stack>
							<Box
								sx={{
									maxHeight: 280,
									overflowY: 'auto',
									border: '1px solid #e2e8f0',
									borderRadius: 2,
									bgcolor: '#fff',
								}}
							>
								{revendaSearchLoading ? (
									<Stack alignItems="center" justifyContent="center" py={5} spacing={1.25}>
										<CircularProgress size={24} sx={{ color: '#0891b2' }} />
										<Typography fontSize="0.75rem" color="#94a3b8">
											Buscando revendas...
										</Typography>
									</Stack>
								) : revendas.length === 0 ? (
									<Stack alignItems="center" justifyContent="center" py={5} spacing={1}>
										<Search sx={{ fontSize: 28, color: '#cbd5e1' }} />
										<Typography fontSize="0.8rem" color="#64748b" fontWeight={600}>
											Nenhuma revenda encontrada
										</Typography>
										<Typography fontSize="0.7rem" color="#94a3b8">
											Tente outro e-mail para filtrar a lista
										</Typography>
									</Stack>
								) : (
									revendas.map((item) => {
										const selected = selectedRevendaId === item.id_revenda;
										return (
											<Box
												key={item.id_revenda}
												onClick={() => setSelectedRevendaId(item.id_revenda)}
												px={2}
												py={1.5}
												sx={{
													cursor: 'pointer',
													display: 'flex',
													alignItems: 'center',
													gap: 1.25,
													borderBottom: '1px solid #f1f5f9',
													borderLeft: selected ? '3px solid #0891b2' : '3px solid transparent',
													bgcolor: selected ? 'rgba(8, 145, 178, 0.08)' : 'white',
													'&:hover': { bgcolor: selected ? 'rgba(8, 145, 178, 0.12)' : '#f8fafc' },
												}}
											>
												<Box
													sx={{
														width: 30,
														height: 30,
														borderRadius: 1.5,
														bgcolor: selected ? '#0891b2' : '#f1f5f9',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														flexShrink: 0,
													}}
												>
													<Storefront sx={{ fontSize: 16, color: selected ? '#fff' : '#94a3b8' }} />
												</Box>
												<Typography fontSize="0.85rem" fontWeight={selected ? 700 : 500} color="#1e293b" sx={{ flex: 1 }}>
													{item.revenda || 'Revenda sem nome'}
												</Typography>
												{selected && <CheckCircle sx={{ fontSize: 18, color: '#0891b2' }} />}
											</Box>
										);
									})
								)}
							</Box>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
					<Button onClick={handleCloseCompradorDialog} disabled={updatingBuyer} sx={{ color: '#64748b' }}>
						Cancelar
					</Button>
					<SubmitButton
						title="Atualizar comprador"
						onClick={() => setConfirmCompradorOpen(true)}
						disabled={updatingBuyer || selectedRevendaId == null}
						maxWidth={220}
					/>
				</DialogActions>
			</Dialog>

			<Dialog
				open={confirmCompradorOpen}
				onClose={() => !updatingBuyer && setConfirmCompradorOpen(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 4,
						overflow: 'hidden',
						boxShadow: '0 25px 50px -12px rgba(0,0,0,.18)',
					},
				}}
				sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
			>
				<Box
					sx={{
						background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
						px: 3,
						pt: 2.5,
						pb: 2.5,
						display: 'flex',
						alignItems: 'center',
						gap: 1.75,
					}}
				>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: 2,
							bgcolor: 'rgba(255,255,255,.2)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<WarningAmberRounded sx={{ fontSize: 22, color: '#fff' }} />
					</Box>
					<Box>
						<Typography fontWeight={700} fontSize="1.05rem" color="#fff">
							Confirmar alteração
						</Typography>
						<Typography fontSize="0.75rem" color="rgba(255,255,255,.85)">
							Essa ação troca o comprador do anúncio
						</Typography>
					</Box>
				</Box>
				<DialogContent sx={{ px: 3, pt: 2.5 }}>
					<Typography fontSize="0.85rem" color="#475569" mb={2}>
						Deseja realmente atualizar o comprador deste anúncio?
					</Typography>
					<Stack spacing={1.25}>
						<Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
							<Typography fontSize="0.65rem" fontWeight={700} color="#94a3b8" textTransform="uppercase" letterSpacing={0.7} mb={0.5}>
								Comprador atual
							</Typography>
							<Typography fontSize="0.825rem" fontWeight={600} color="#64748b">
								{paymentToChangeBuyer?.revenda?.revenda || 'Revenda não informada'}
							</Typography>
						</Box>
						<Stack alignItems="center">
							<ArrowForward sx={{ fontSize: 18, color: '#0891b2' }} />
						</Stack>
						<Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ecfeff', border: '1px solid #a5f3fc' }}>
							<Typography fontSize="0.65rem" fontWeight={700} color="#0e7490" textTransform="uppercase" letterSpacing={0.7} mb={0.5}>
								Novo comprador
							</Typography>
							<Typography fontSize="0.825rem" fontWeight={700} color="#0f172a">
								{revendas.find((item) => item.id_revenda === selectedRevendaId)?.revenda || 'esta revenda'}
							</Typography>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<Button onClick={() => setConfirmCompradorOpen(false)} disabled={updatingBuyer} sx={{ color: '#64748b' }}>
						Não
					</Button>
					<SubmitButton title="Sim, atualizar" onClick={handleAtualizarComprador} disabled={updatingBuyer} maxWidth={160} />
				</DialogActions>
			</Dialog>

			{/* ── Main Content ───────────────────────────────── */}
			<Box display="flex" flexDirection="column" gap={2}>
				{/* ── Stat Cards ─────────────────────────────── */}
				<Box
					display="grid"
					gridTemplateColumns={isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'}
					gap={2}
					mb={4}
				>
					<StatCard
						value={dashboard?.qtd_pago ?? 0}
						label="Pagos"
						icon={<CheckCircle sx={{ fontSize: 20, color: 'white' }} />}
						colorMain="rgb(5, 150, 104)"
						colorBg="rgba(5, 150, 105, 0.1)"
					/>
					<StatCard
						value={dashboard?.qtd_nao_pago ?? 0}
						label="Pendentes"
						icon={<PaymentsIcon sx={{ fontSize: 20, color: 'white' }} />}
						colorMain="rgb(220, 38, 38)"
						colorBg="rgba(220, 38, 38, 0.1)"
					/>
					<StatCard
						value={formatCurrency(dashboard?.val_cobrado ?? 0)}
						label="Total Cobrado"
						icon={<AccountBalance sx={{ fontSize: 20, color: 'white' }} />}
						colorMain="rgb(37, 100, 235)"
						colorBg="rgba(37, 99, 235, 0.1)"
					/>
					<StatCard
						value={formatCurrency(dashboard?.val_recebido ?? 0)}
						label="Total Recebido"
						icon={<TrendingUp sx={{ fontSize: 20, color: 'white' }} />}
						colorMain="rgb(255, 106, 0)"
						colorBg="rgba(255, 107, 0, 0.1)"
					/>
				</Box>

				{/* ── Table Container ────────────────────────── */}
				<Box
					bgcolor="white"
					borderRadius={3}
					border="1px solid #e2e8f0"
					overflow="hidden"
					sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
				>
					{/* Header / Search / Date Filters */}
					<Box
						px={isMobile ? 3 : 4}
						py={3}
						borderBottom="1px solid #f1f5f9"
						display="flex"
						flexDirection="column"
						gap={2}
					>
						<Box
							display="flex"
							flexDirection={isMobile ? 'column' : 'row'}
							justifyContent="space-between"
							alignItems={isMobile ? 'stretch' : 'center'}
							gap={2}
						>
							<Typography fontSize="1.325rem" fontWeight={700}>
								Pagamentos
							</Typography>
							<TextField
								size="small"
								placeholder="Buscar veículo, revenda, placa..."
								value={searchTerm}
							onChange={(e) => {
								const value = e.target.value;
								setSearchTerm(value);
								searchInput.current = value;
								if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
								debounceTimeout.current = setTimeout(() => {
									fetchPaymentsData();
								}, 700);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
									fetchPaymentsData();
								}
							}}
								sx={{ ...textFieldStyle, minWidth: isMobile ? 'auto' : 280 }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search sx={{ fontSize: 18, color: '#94a3b8' }} />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						{/* Date filters + filter chips */}
						<Box
							display="flex"
							flexDirection={isMobile ? 'column' : 'row'}
							gap={2}
							alignItems={isMobile ? 'stretch' : 'center'}
						>
							<TextField
								size="small"
								label="Data Venda Inicial"
								type="date"
								value={startDate}
								onChange={(e) => {
									setStartDate(e.target.value);
									startDateRef.current = e.target.value;
								}}
								sx={{ ...textFieldStyle, width: isMobile ? '100%' : 200 }}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								size="small"
								label="Data Venda Final"
								type="date"
								value={endDate}
								onChange={(e) => {
									setEndDate(e.target.value);
									endDateRef.current = e.target.value;
								}}
								inputProps={{ min: startDate }}
								sx={{ ...textFieldStyle, width: isMobile ? '100%' : 200 }}
								InputLabelProps={{ shrink: true }}
							/>
							<Box
							onClick={() => fetchPaymentsData()}
							sx={{
								cursor: 'pointer',
								'&:hover': { opacity: 0.85 },
							}}
							>
							<Search/>
							</Box>
					
							<Stack direction="row" spacing={1} ml={isMobile ? 0 : 'auto'}>
								<Chip
									label="Todos"
									size="small"
									onClick={() => handleFilterChange('all')}
									sx={{
										bgcolor: activeFilter === 'all' ? colorPrimary : '#f1f5f9',
										color: activeFilter === 'all' ? 'white' : '#475569',
										fontWeight: 700,
										fontSize: '0.6875rem',
										cursor: 'pointer',
										'&:hover': { opacity: 0.85 },
									}}
								/>
								<Chip
									label="Pendentes"
									size="small"
									onClick={() => handleFilterChange('open')}
									sx={{
										bgcolor: activeFilter === 'open' ? '#dc2626' : '#f1f5f9',
										color: activeFilter === 'open' ? 'white' : '#475569',
										fontWeight: 700,
										fontSize: '0.6875rem',
										cursor: 'pointer',
										'&:hover': { opacity: 0.85 },
									}}
								/>
								<Chip
									label="Pagos"
									size="small"
									onClick={() => handleFilterChange('closed')}
									sx={{
										bgcolor: activeFilter === 'closed' ? '#059669' : '#f1f5f9',
										color: activeFilter === 'closed' ? 'white' : '#475569',
										fontWeight: 700,
										fontSize: '0.6875rem',
										cursor: 'pointer',
										'&:hover': { opacity: 0.85 },
									}}
								/>
							</Stack>
						</Box>
					</Box>

					{/* Table */}
					<TableContainer sx={{ overflowX: 'auto' }}>
						<Table sx={{ minWidth: 1100 }}>
							<TableHead>
								<TableRow sx={{ bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
									<TableCell sx={{ ...headerCellSx, width: '25%' }}>Veículo</TableCell>
									<TableCell sx={{ ...headerCellSx, width: '12%' }}>Concessionária</TableCell>
									<TableCell sx={{ ...headerCellSx, width: '10%' }}>Revenda</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '8%' }}>Data Venda</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '8%' }}>Vencimento</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '8%' }}>Data Pgto</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '7%' }}>Cobrado</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '7%' }}>Pago</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '8%' }}>Situação</TableCell>
									<TableCell align="center" sx={{ ...headerCellSx, width: '10%' }}>Ações</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedPayments.length === 0 ? (
									<TableRow>
										<TableCell colSpan={10} align="center" sx={{ py: 8, borderBottom: 'none' }}>
											<Typography fontSize="1rem" fontWeight={500} color={colorSubText}>
												{searchTerm
													? 'Nenhum resultado encontrado para sua busca.'
													: 'Nenhum pagamento encontrado no período selecionado.'}
											</Typography>
										</TableCell>
									</TableRow>
								) : (
									paginatedPayments.map((payment, index) => (
										<React.Fragment key={payment.id_pagamento}>
											{/* ── Main Row ── */}
											<TableRow
												sx={{
													bgcolor: index % 2 === 1 ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
													transition: 'background-color 0.2s',
													'&:hover': { bgcolor: 'rgba(248, 250, 252, 0.8)' },
												}}
											>
												{/* Veículo */}
												<TableCell sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Box display="flex" alignItems="flex-start" gap={2}>
														{/* Icon */}
														<Box
															width={48}
															height={48}
															bgcolor="#f1f5f9"
															borderRadius={2}
															display="flex"
															alignItems="center"
															justifyContent="center"
															flexShrink={0}
															position="relative"
															overflow="hidden"
														>
															<DirectionsCar sx={{ fontSize: 22, color: '#94a3b8' }} />
															{payment.veiculos.length > 1 && (
																<Box
																	position="absolute"
																	top={0}
																	left={0}
																	right={0}
																	bottom={0}
																	display="grid"
																	gridTemplateColumns="1fr 1fr"
																	gap="1px"
																	sx={{ opacity: 0.15 }}
																>
																	<Box bgcolor="#94a3b8" />
																	<Box bgcolor="#64748b" />
																</Box>
															)}
														</Box>

														{/* Info */}
														<Box flex={1}>
															<Stack direction="row" alignItems="center" gap={1} mb={0.5}>
																{payment.veiculos.length > 1 && (
																	<Chip
																		label="COMBO"
																		size="small"
																		sx={{
																			bgcolor: colorPrimary,
																			color: 'white',
																			fontSize: '0.5rem',
																			fontWeight: 900,
																			height: 18,
																			textTransform: 'uppercase',
																			fontStyle: 'italic',
																			'& .MuiChip-label': { px: 0.75 },
																		}}
																	/>
																)}
																<Typography fontWeight={700} fontSize="0.8rem" color="#1e293b" noWrap>
																	{`${payment.veiculos[0]?.marca} ${payment.veiculos[0]?.modelo} ${payment.veiculos.length >1? `+ ${payment.veiculos.length - 1}` : ''}`}
																</Typography>
															</Stack>

															{payment.veiculos.length > 1 ? (
																<>
																	<Typography fontSize="0.6875rem" color="#94a3b8" mb={0.5}>
																		{payment.veiculos.length} Veículos no pacote
																	</Typography>
																	<Box
																		display="inline-flex"
																		alignItems="center"
																		gap={0.5}
																		sx={{ cursor: 'pointer' }}
																		onClick={() => toggleRowExpansion(payment.id_pagamento)}
																	>
																		<Typography
																			fontSize="0.625rem"
																			fontWeight={700}
																			color={colorPrimary}
																			textTransform="uppercase"
																			letterSpacing={0.5}
																			sx={{ '&:hover': { color: '#ea580c' } }}
																		>
																			Detalhes
																		</Typography>
																		<ExpandMore
																			sx={{
																				fontSize: 14,
																				color: colorPrimary,
																				transform: expandedRows.includes(payment.id_pagamento)
																					? 'rotate(180deg)'
																					: 'rotate(0deg)',
																				transition: 'transform 0.3s',
																			}}
																		/>
																	</Box>
																</>
															) : (
																<Typography fontSize="0.6875rem" color="#94a3b8" fontWeight={500}>
																	{payment.veiculos[0]?.placa}
																</Typography>
															)}
														</Box>
													</Box>
												</TableCell>

												{/* Concessionária */}
												<TableCell sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography fontSize="0.825rem" fontWeight={500} color="#64748b">
														{payment.concessionaria}
													</Typography>
												</TableCell>

												{/* Revenda */}
												<TableCell sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography
														fontSize="0.825rem"
														fontWeight={600}
														color={colorPrimary}
														sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
														onClick={() => handleOpenBuyerDialog(payment.revenda)}
													>
														{payment.revenda.revenda}
													</Typography>
												</TableCell>

												{/* Data Venda */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography fontSize="0.825rem" fontWeight={500} color="#64748b">
														{formatDate(payment.data_venda)}
													</Typography>
												</TableCell>

												{/* Vencimento */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography fontSize="0.825rem" fontWeight={500} color="#64748b">
														{formatDate(payment.data_vencimento)}
													</Typography>
												</TableCell>

												{/* Data Pgto */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography fontSize="0.825rem" fontWeight={500} color="#64748b">
														{formatDate(payment.data_pagamento)}
													</Typography>
												</TableCell>

												{/* Cobrado */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography fontSize="0.875rem" fontWeight={900} color="#1e293b">
														{formatCurrency(payment.val_cobrado)}
													</Typography>
												</TableCell>

												{/* Pago */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Typography
														fontSize="0.875rem"
														fontWeight={900}
														color={payment.val_pagamento ? '#059669' : '#94a3b8'}
													>
														{payment.val_pagamento ? formatCurrency(payment.val_pagamento) : '-'}
													</Typography>
												</TableCell>

												{/* Situação */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Stack alignItems="center" gap={0.75}>
														<Chip
															label={payment.data_pagamento ? 'Pago' : 'Pendente'}
															size="small"
															sx={{
																bgcolor: payment.data_pagamento ? '#dcfce7' : '#fee2e2',
																color: payment.data_pagamento ? '#15803d' : '#b91c1c',
																fontSize: '0.5625rem',
																fontWeight: 700,
																height: 24,
																textTransform: 'uppercase',
																letterSpacing: 1,
																minWidth: 80,
																'& .MuiChip-label': { px: 1.25 },
															}}
														/>
														{payment.anuncio_renovado && (
															<Chip
																label="Renovado"
																size="small"
																sx={{
																	bgcolor: '#dbeafe',
																	color: '#1e40af',
																	fontSize: '0.5625rem',
																	fontWeight: 700,
																	height: 24,
																	textTransform: 'uppercase',
																	letterSpacing: 1,
																	minWidth: 80,
																	'& .MuiChip-label': { px: 1.25 },
																}}
															/>
														)}
													</Stack>
												</TableCell>

												{/* Ações */}
												<TableCell align="center" sx={{ py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
													<Stack direction="row" justifyContent="center" gap={0.5}>
														<Tooltip title="PIX QR Code" arrow>
															<IconButton
																size="small"
																onClick={() => handleOpenQrCode(payment)}
																sx={{
																	width: 30,
																	height: 30,
																	bgcolor: '#059669',
																	color: 'white',
																	borderRadius: 1.5,
																	'&:hover': { bgcolor: '#047857', transform: 'scale(1.05)' },
																	transition: 'all 0.2s',
																}}
															>
																<QrCode size={16} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Dar Baixa" arrow>
															<IconButton
																size="small"
																onClick={() => handleOpenPaidDialog(payment)}
																sx={{
																	width: 30,
																	height: 30,
																	bgcolor: '#2563eb',
																	color: 'white',
																	borderRadius: 1.5,
																	'&:hover': { bgcolor: '#1d4ed8', transform: 'scale(1.05)' },
																	transition: 'all 0.2s',
																}}
															>
																<AttachMoneyOutlined sx={{ fontSize: 16 }} />
															</IconButton>
														</Tooltip>
													<Tooltip title="Gerar Nova Cobrança" arrow>
														<IconButton
															size="small"
															onClick={() => handleRemakeConfirm(payment)}
															sx={{
																width: 30,
																height: 30,
																bgcolor: colorPrimary,
																color: 'white',
																borderRadius: 1.5,
																'&:hover': { bgcolor: '#ea580c', transform: 'scale(1.05)' },
																transition: 'all 0.2s',
															}}
														>
															<CurrencyExchange sx={{ fontSize: 16 }} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Atualizar Cobrança" arrow>
														<IconButton
															size="small"
															onClick={() => handleOpenUpdateDialog(payment)}
															sx={{
																width: 30,
																height: 30,
																bgcolor: '#7c3aed',
																color: 'white',
																borderRadius: 1.5,
																'&:hover': { bgcolor: '#6d28d9', transform: 'scale(1.05)' },
																transition: 'all 0.2s',
															}}
														>
															<EditOutlined sx={{ fontSize: 16 }} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Atualizar comprador" arrow>
														<IconButton
															size="small"
															onClick={() => handleOpenCompradorDialog(payment)}
															sx={{
																width: 30,
																height: 30,
																bgcolor: '#0891b2',
																color: 'white',
																borderRadius: 1.5,
																'&:hover': { bgcolor: '#0e7490', transform: 'scale(1.05)' },
																transition: 'all 0.2s',
															}}
														>
															<Storefront sx={{ fontSize: 16 }} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Excluir" arrow>
															<IconButton
																size="small"
																onClick={() => handleDeleteConfirm(payment.id_pagamento)}
																sx={{
																	width: 30,
																	height: 30,
																	bgcolor: '#dc2626',
																	color: 'white',
																	borderRadius: 1.5,
																	'&:hover': { bgcolor: '#b91c1c', transform: 'scale(1.05)' },
																	transition: 'all 0.2s',
																}}
															>
																<DeleteOutlined sx={{ fontSize: 16 }} />
															</IconButton>
														</Tooltip>
													</Stack>
												</TableCell>
											</TableRow>

											{/* ── Expanded Row (Combo Details) ── */}
											{payment.veiculos.length > 1 && (
												<TableRow>
													<TableCell
														colSpan={10}
														sx={{
															py: 0,
															borderBottom: '1px solid #f1f5f9',
															bgcolor: index % 2 === 1 ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
														}}
													>
														<Collapse in={expandedRows.includes(payment.id_pagamento)} timeout="auto" unmountOnExit>
															<Box py={2} pl={9} pr={4}>
																<Stack spacing={1} borderLeft={`2px solid ${colorPrimary}33`} pl={2}>
																	{payment.veiculos.map((veiculo) => (
																		<Box
																			key={veiculo.placa}
																			bgcolor="white"
																			p={1.5}
																			borderRadius={1.5}
																			border="1px solid #f1f5f9"
																			sx={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
																		>
																			<Stack
																				direction={isMobile ? 'column' : 'row'}
																				justifyContent="space-between"
																				alignItems={isMobile ? 'flex-start' : 'center'}
																				gap={1}
																			>
																				<Box>
																					<Typography fontSize="0.825rem" fontWeight={700} mb={0.25}>
																						{veiculo.marca} {veiculo.modelo}
																					</Typography>
																					<Typography fontSize="0.75rem" color="#64748b">
																						{veiculo.ano_fabricacao}/{veiculo.ano_modelo} &bull; {veiculo.km} km &bull;{' '}
																						{veiculo.transmissao} &bull; {veiculo.combustivel}
																					</Typography>
																				</Box>
																				<Chip
																					label={veiculo.placa}
																					size="small"
																					sx={{
																						bgcolor: '#f1f5f9',
																						color: '#475569',
																						fontWeight: 700,
																						fontSize: '0.6875rem',
																						letterSpacing: 1,
																					}}
																				/>
																			</Stack>
																		</Box>
																	))}
																</Stack>
															</Box>
														</Collapse>
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* ── Pagination Footer ──────────────────── */}
					<Box
						px={4}
						py={2}
						borderTop="1px solid #f1f5f9"
						bgcolor="rgba(248, 250, 252, 0.3)"
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						flexDirection={isMobile ? 'column' : 'row'}
						gap={2}
					>
						<Typography fontSize="0.625rem" fontWeight={500} color="#94a3b8" textTransform="uppercase" letterSpacing={1}>
							Mostrando {payments.length === 0 ? 0 : startIndex + 1}-
							{Math.min(endIndex, payments.length)} de {payments.length} resultados
						</Typography>
						<Stack direction="row" spacing={0.5}>
							<IconButton
								size="small"
								onClick={handlePrevPage}
								disabled={currentPage === 1}
								sx={{
									width: 32,
									height: 32,
									border: '1px solid #e2e8f0',
									borderRadius: 1.5,
									color: currentPage === 1 ? '#cbd5e1' : '#94a3b8',
									'&:hover': { bgcolor: currentPage === 1 ? 'transparent' : 'white' },
									'&:disabled': { color: '#cbd5e1' },
								}}
							>
								<ChevronLeft sx={{ fontSize: 18 }} />
							</IconButton>
							<Box
								width={32}
								height={32}
								bgcolor={colorPrimary}
								color="white"
								borderRadius={1.5}
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								<Typography fontSize="0.75rem" fontWeight={700}>
									{currentPage}
								</Typography>
							</Box>
							{totalPages > 1 && (
								<Typography fontSize="0.75rem" fontWeight={500} color="#94a3b8" display="flex" alignItems="center" px={1}>
									de {totalPages}
								</Typography>
							)}
							<IconButton
								size="small"
								onClick={handleNextPage}
								disabled={currentPage === totalPages}
								sx={{
									width: 32,
									height: 32,
									border: '1px solid #e2e8f0',
									borderRadius: 1.5,
									color: currentPage === totalPages ? '#cbd5e1' : '#94a3b8',
									'&:hover': { bgcolor: currentPage === totalPages ? 'transparent' : 'white' },
									'&:disabled': { color: '#cbd5e1' },
								}}
							>
								<ChevronRight sx={{ fontSize: 18 }} />
							</IconButton>
						</Stack>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default Payments;
